import type { UseI18nResult } from '@bravemove/i18n/react/core';
import type { VerifiableEntityType } from './entityTypes';

/**
 * F09 v2 tick copy. Keys live in the shared `verification` namespace (frozen
 * W1 contract). Every lookup passes the exact English `defaultValue` so the
 * primitive renders correctly without an i18n provider or before the
 * namespace loads.
 */

type TFn = UseI18nResult['t'];

export const TICK_LABEL_GENERIC_DEFAULT = 'Verified';

export const TICK_LABEL_DEFAULTS: Readonly<Record<VerifiableEntityType, string>> = {
  player: 'Verified player',
  trainer: 'Verified trainer',
  venue: 'Verified venue',
  club: 'Verified club',
  organizer: 'Verified organizer',
};

export const TICK_TOOLTIP_DEFAULTS: Readonly<Record<VerifiableEntityType, string>> = {
  player: 'Verified player · How verification works',
  trainer: 'Verified trainer · How verification works',
  venue: 'Verified venue · How verification works',
  club: 'Verified club · How verification works',
  organizer: 'Verified organizer · How verification works',
};

export const TICK_HERO_CRITERIA_DEFAULTS: Readonly<Record<VerifiableEntityType, string>> = {
  player: 'Identity and notability checked by BraveMove',
  trainer: 'Identity and credentials checked by BraveMove',
  venue: 'Business and ownership checked by BraveMove',
  club: 'Registration and ownership checked by BraveMove',
  organizer: 'Registration and ownership checked by BraveMove',
};

export const TICK_HERO_SINCE_DEFAULT = 'Since {date}';

/** `tick.label.<type>` — or `tick.label.generic` when no entity type is known. */
export function tickLabel(t: TFn, type: VerifiableEntityType | undefined): string {
  if (!type) {
    return String(
      t('verification:tick.label.generic', { defaultValue: TICK_LABEL_GENERIC_DEFAULT }),
    );
  }
  return String(t(`verification:tick.label.${type}`, { defaultValue: TICK_LABEL_DEFAULTS[type] }));
}

/**
 * `tick.tooltip.<type>`. The contract has no generic tooltip key, so an
 * untyped tick falls back to its label ("Verified").
 */
export function tickTooltip(t: TFn, type: VerifiableEntityType | undefined): string {
  if (!type) return tickLabel(t, undefined);
  return String(
    t(`verification:tick.tooltip.${type}`, { defaultValue: TICK_TOOLTIP_DEFAULTS[type] }),
  );
}

/** `tick.hero.criteria.<type>` — the hero card subline. */
export function tickHeroCriteria(t: TFn, type: VerifiableEntityType): string {
  return String(
    t(`verification:tick.hero.criteria.${type}`, {
      defaultValue: TICK_HERO_CRITERIA_DEFAULTS[type],
    }),
  );
}

/**
 * `tick.hero.since` with `{date}` interpolated. Without an i18next instance
 * react-i18next returns the raw `defaultValue`, so the placeholder is also
 * substituted manually (a no-op when i18next already interpolated it).
 */
export function tickHeroSince(t: TFn, formattedDate: string): string {
  const raw = String(
    t('verification:tick.hero.since', {
      date: formattedDate,
      defaultValue: TICK_HERO_SINCE_DEFAULT,
    }),
  );
  return raw.replace('{date}', formattedDate);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse the `since` ISO string. Date-only values (`YYYY-MM-DD`) are formatted
 * in UTC so the calendar day never shifts with the viewer's offset. Returns
 * `null` for empty / unparseable input (the since line is then omitted).
 */
export function parseSince(
  since: string | null | undefined,
): { readonly date: Date; readonly utc: boolean } | null {
  if (!since) return null;
  const date = new Date(since);
  if (Number.isNaN(date.getTime())) return null;
  return { date, utc: DATE_ONLY.test(since) };
}
